/**
 * Loads the Pendo analytics agent. This is Pendo's install snippet, moved out of
 * `index.html` so that `main.ts` can run it after {@link captureResetToken} has
 * taken a set-password link's token out of the address bar. Run from the HTML,
 * the agent could load while the token was still in `location.hash`.
 *
 * The stub queues calls until the agent arrives, so `login.component.ts` can call
 * `pendo.initialize` as before. The key is the same in every environment;
 * visitor ids carry `environment.env` to tell traffic apart.
 */
declare global {
  interface Window {
    pendo: any;
  }
}

const PENDO_API_KEY ='f30dafb7-8e01-4c0e-5372-2b6bd408d128';
const PENDO_METHODS = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];

export function loadPendo(win: Window = window, doc: Document = document): void {
  const pendo = (win.pendo = win.pendo || {});
  pendo._q = pendo._q || [];
  for (const method of PENDO_METHODS) {
    pendo[method] =
      pendo[method] ||
      function (...args: unknown[]) {
        // initialize goes first, so the agent applies it before anything queued earlier.
        pendo._q[method === 'initialize' ? 'unshift' : 'push']([method, ...args]);
      };
  }

  const script = doc.createElement('script');
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${PENDO_API_KEY}/pendo.js`;
  const firstScript = doc.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    doc.head.appendChild(script);
  }
}
